import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';
import { Water } from 'three/examples/jsm/objects/Water.js';
import {
    EffectComposer,
    EffectPass,
    RenderPass,
    BloomEffect,
    VignetteEffect,
    SMAAEffect,
    SMAAPreset,
    ToneMappingEffect,
    ToneMappingMode,
    SSAOEffect,
    NormalPass
} from 'postprocessing';
import * as dat from 'dat.gui';
import {CameraManager} from "./camera_manager.ts";
import {RenderManager} from "./render_manager.ts";
import {Popup3D} from "./Popup3D.ts";

// @ts-ignore
import markup from './popup.html?raw';

// Type definitions
interface DevSettings {
    exposure: number;
    toneMapping: string;
    ambientIntensity: number;
    ambientColor: string;
    directIntensity: number;
    directColor: string;
    punctualLights: boolean;
    envIntensity: number;
    showEnvBackground: boolean;
    bloomEnabled: boolean;
    bloomIntensity: number;
    bloomLuminanceThreshold: number;
    bloomLuminanceSmoothing: number;
    bloomRadius: number;
    vignetteEnabled: boolean;
    vignetteOffset: number;
    vignetteDarkness: number;
    ssaoEnabled: boolean;
    ssaoIntensity: number;
    ssaoRadius: number;
    ssaoSamples: number;
    ssaoBias: number;
    modelScale: number;
    topViewZoom: number;
    topViewMinZoom: number;
    topViewMaxZoom: number;
    fitToView: () => void;
    logCamera: () => void;
}

interface GUIFolders {
    cameras: dat.GUI;
    pointLights: dat.GUI;
    spotLights: dat.GUI;
}

// Hotspot interface
interface Hotspot {
    id: string;
    position: THREE.Vector3;
    title: string;
    description: string;
    mesh: THREE.Mesh;
    label: HTMLElement;
    // Apartment linking
    apartmentIndex: number | null;  // null = no link, 1-3 = apartment index
    mediaUrl: string;               // optional image/video URL
    mediaType: 'image' | 'video' | null;
    color: string;                  // hex color for hotspot marker
}

// Extend Window interface
declare global {
    interface Window {
        guiFolders: GUIFolders;
        hotspots: Hotspot[];
        hotspotEditMode: boolean;
    }
}

// Console error tracking
window.addEventListener('error', (e: ErrorEvent) => {
    console.error('Global error:', e.message, e.filename, e.lineno, e.colno);
});

// Three.js variables
let scene: THREE.Scene;
let cameraManager: CameraManager;
let renderManager: RenderManager;
let model: THREE.Group;
let ambientLight: THREE.AmbientLight;
let glbLights: THREE.Light[] = [];
let gui: dat.GUI;
let tennisBalls: THREE.Object3D[] = [];
let envMap: THREE.Texture;

// Cameras and lights from GLB
let glbCameras: THREE.Camera[] = [];
let glbPointLights: THREE.PointLight[] = [];
let glbSpotLights: THREE.SpotLight[] = [];

// Track if camera controls are locked (for Top View)
let cameraLocked = false;

// Hotspot system
let hotspots: Hotspot[] = [];
let hotspotEditMode = false;
let hotspotContainer: HTMLElement;
let selectedHotspot: Hotspot | null = null;
let defaultHotspotColor = '#ffd700'; // Gold/yellow default

// Apartment model system
interface ApartmentModel {
    name: string;
    file: string;
    model: THREE.Group | null;
    loaded: boolean;
}

const apartmentModels: ApartmentModel[] = [
    { name: 'None (Main View)', file: '', model: null, loaded: true },
    { name: 'Ocean View Suite', file: 'apartments/MiamiBeach_OceanView-nomaterials.glb', model: null, loaded: false },
    { name: 'Beachfront Residence', file: 'apartments/MiamiBeach_Beachfront-nomaterials.glb', model: null, loaded: false },
    { name: 'Penthouse Collection', file: 'apartments/MiamiBeach_Penthouse-nomaterials.glb', model: null, loaded: false }
];

let currentApartment: ApartmentModel | null = null;
let isTransitioning = false;

// Helicopter animation system
let choppersModel: THREE.Group | null = null;
let chopperRotors: THREE.Object3D[] = [];
let chopperStartTime = 0;
const chopperConfig = {
    hoverHeight: 0.03,       // Subtle bobbing height
    hoverSpeed: 1.5,         // Gentle bobbing speed
    circleRadius: 1.2,       // Flight circle radius
    circleSpeed: 0.08,       // Slower, more realistic circling
    tiltAmount: 0.15,        // Bank angle when turning
    rotorSpeed: 25,          // Rotor rotation speed (radians per second)
    altitudeVariation: 0.1,  // Slight altitude changes during flight
    driftAmount: 0.2         // Random drift for natural movement
};

// Transport and Pinpoints models for toggle/animation
let transportModel: THREE.Group | null = null;
let pinpointsModel: THREE.Group | null = null;
let pinpointsStartTime = 0;

// Ocean water shader
let oceanWater: Water | null = null;
let allWaterMeshes: Water[] = []; // Store all water meshes for animation

// Water settings for GUI control
const waterSettings = {
    color: '#001572',
    sunColor: '#fff7bb',
    distortionScale: 0.1,
    speed: 0.2,
    size: 50,
    alpha: 0.76,
    sunDirectionX: 0,
    sunDirectionY: -1,
    sunDirectionZ: 0
};

// Pinpoint image configuration
interface PinpointConfig {
    meshName: string;       // Name of the mesh in the GLB to target
    imageUrl: string;       // URL of the circular image to display
    label?: string;         // Optional label for the pinpoint
}

// Configurable pinpoint images - add your pinpoint configurations here
// Mesh names found in MiamiBeach_Pinpoints.glb:
//   - MAW_pinpoint-shutterstop_1
//   - MAW_pinpoint-shutterstop_2
//   - MAW_pinpoint-shutterstop_3
const pinpointConfigs: PinpointConfig[] = [
    { meshName: 'MAW_pinpoint-shutterstop_1', imageUrl: '/images/pinpoint1.svg', label: 'Location 1' },
    { meshName: 'MAW_pinpoint-shutterstop_2', imageUrl: '/images/pinpoint2.svg', label: 'Location 2' },
    { meshName: 'MAW_pinpoint-shutterstop_3', imageUrl: '/images/pinpoint3.svg', label: 'Location 3' },
];

// Store pinpoint image meshes for potential interaction
const pinpointImageMeshes: THREE.Mesh[] = [];

// Apartment Popup System
let apartmentPopupActive = false;
let apartmentPopupRenderer: THREE.WebGLRenderer | null = null;
let apartmentPopupScene: THREE.Scene | null = null;
let apartmentPopupCamera: THREE.PerspectiveCamera | null = null;
let apartmentPopupControls: OrbitControls | null = null;
let apartmentPopupComposer: EffectComposer | null = null;
let currentPopupHotspot: Hotspot | null = null;
let mainRenderPaused = false;

// Initialize hotspot globals
window.hotspots = hotspots;
window.hotspotEditMode = hotspotEditMode;

// Dev controls settings
const devSettings: DevSettings = {
    exposure: 0,
    toneMapping: 'Linear',
    ambientIntensity: 0.3,
    ambientColor: '#ffffff',
    directIntensity: 2.5,
    directColor: '#ffffff',
    punctualLights: true,
    envIntensity: 2.0,
    showEnvBackground: false,
    bloomEnabled: true,
    bloomIntensity: 0.17,
    bloomLuminanceThreshold: 1,
    bloomLuminanceSmoothing: 0.025,
    bloomRadius: 0.44,
    vignetteEnabled: true,
    vignetteOffset: 0.3,
    vignetteDarkness: 0.5,
    ssaoEnabled: true,
    ssaoIntensity: 1.5,
    ssaoRadius: 0.08,
    ssaoSamples: 8, // Reduced from 16 for performance
    ssaoBias: 0.025,
    modelScale: 2.5,
    topViewZoom: 1.5,
    topViewMinZoom: 20,
    topViewMaxZoom: 150,
    fitToView: () => cameraManager.fitToModel(model),
    logCamera: () => cameraManager.logCameraState()
};

// Apply circular images to pinpoint meshes
function applyPinpointImages(pinpointsModel: THREE.Group, circularMeshes: THREE.Mesh[]): void {
    const textureLoader = new THREE.TextureLoader();

    // If we have specific configurations, apply them
    pinpointConfigs.forEach((config) => {
        // Find the mesh by name in the model
        let targetMesh: THREE.Mesh | null = null;
        pinpointsModel.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh && child.name === config.meshName) {
                targetMesh = child as THREE.Mesh;
            }
        });

        if (targetMesh && config.imageUrl) {
            console.log(`Applying image to pinpoint: ${config.meshName}`);
            textureLoader.load(
                config.imageUrl,
                (texture) => {
                    // Create a circular material with the image
                    const material = new THREE.MeshBasicMaterial({
                        map: texture,
                        transparent: true,
                        side: THREE.DoubleSide,
                        depthWrite: false
                    });

                    // Apply circular clipping using alpha
                    texture.colorSpace = THREE.SRGBColorSpace;

                    if (targetMesh) {
                        targetMesh.material = material;
                        pinpointImageMeshes.push(targetMesh);
                        console.log(`Applied image to ${config.meshName}`);
                    }
                },
                undefined,
                (error) => console.warn(`Failed to load pinpoint image for ${config.meshName}:`, error)
            );
        }
    });

    // If no configs but we found circular meshes, log them for user reference
    if (pinpointConfigs.length === 0 && circularMeshes.length > 0) {
        console.log('No pinpoint image configs found. Available meshes to configure:');
        circularMeshes.forEach((mesh, index) => {
            console.log(`  ${index + 1}. "${mesh.name}" - Add to pinpointConfigs array in main.ts`);
        });
    }
}

let popup3D: Popup3D;

// Initialize Three.js
function initThreeJS(): void {
    const container = document.getElementById('threejs-container');
    const loadingScreen = document.getElementById('loading-screen');
    const logoFillWrapper = document.getElementById('logo-fill-wrapper');
    const progressText = document.getElementById('progress-text');

    if (!container) {
        console.error('Container not found');
        return;
    }

    // Create scene
    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x191919);

    const hdrLoader = new HDRLoader();

    hdrLoader.load('/textures/HdrSkySunset007_HDR_2K.hdr', (texture) => {
        texture.mapping = THREE.EquirectangularReflectionMapping;

        scene.environment = texture; // lighting + reflections
        scene.background = texture;  // optional
    });

    // Load equirectangular HDR sky texture as background
    // const textureLoader = new THREE.TextureLoader();
    // textureLoader.load('/HdrSkySunset007_JPG_2K.jpg', (texture) => {
    //     texture.mapping = THREE.EquirectangularReflectionMapping;
    //     texture.colorSpace = THREE.SRGBColorSpace;
    //     scene.background = texture;
    //     console.log('Sky background loaded successfully');
    // });

    cameraManager = new CameraManager();
    renderManager = new RenderManager(container, scene, cameraManager.camera);
    // lightManager = new LightManager(scene);
    // lightManager.setupBaseLighting();
    cameraManager.initOrbitControls(renderManager.domElement);

    document.body.insertAdjacentHTML('beforeend', markup);
    const popupElement = document.querySelector('.popup-wrapper') as HTMLElement;

    popup3D = new Popup3D(popupElement, cameraManager.camera);
    popup3D.shouldTiltX = false;
    popup3D.scale.set(0.001, 0.001, 0.001);
    scene.add(popup3D);


    // Raycaster for click-to-position
    const raycaster = new THREE.Raycaster();
    const tap = new THREE.Vector2();

    // Initialize hotspot container
    hotspotContainer = document.getElementById('hotspot-container') as HTMLElement;
    if (!hotspotContainer) {
        hotspotContainer = document.createElement('div');
        hotspotContainer.id = 'hotspot-container';
        document.body.appendChild(hotspotContainer);
    }

    renderManager.domElement.addEventListener('pointerdown', e => {
        tap.x = e.clientX;
        tap.y = e.clientY;
        console.log("Tap", tap);
    });

    renderManager.domElement.addEventListener('pointerup', e => {
        if (Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > 6) return; // was drag

        tap.x = (e.clientX / window.innerWidth) * 2 - 1;
        tap.y = -(e.clientY / window.innerHeight) * 2 + 1;

        raycaster.setFromCamera(tap, cameraManager.camera);

        popup3D.setVisible(false);

        if (pinpointsModel) {
            const intersects = raycaster.intersectObjects(pinpointsModel.children, true);

            if (intersects.length > 0) {
                const clickedObject = intersects[0].object;

                // 1. Create a Box3 to calculate the bounds
                const box = new THREE.Box3().setFromObject(clickedObject);
                const center = new THREE.Vector3();
                box.getCenter(center); // This gets the geometric center

                console.log(`Object Center:`, center);

                if (hotspotEditMode) {
                    createHotspot(center);
                }

                popup3D.position.copy(center);
                popup3D.position.y = box.max.y;
                popup3D.setVisible(true);

                center.y = box.max.y + 0.15;

                cameraManager.flyToPointCalcAzimut(center, 1, Math.PI / 2);
            } else {
                cameraManager.flyToPointCalcAzimut(new THREE.Vector3(0, 0.1, 0), 1.5, Math.PI / 3);
            }
        }
    });

    // Lighting setup
    ambientLight = new THREE.AmbientLight(
        new THREE.Color(devSettings.ambientColor),
        devSettings.ambientIntensity
    );
    scene.add(ambientLight);

    // Directional light
    const directionalLight = new THREE.DirectionalLight(
        new THREE.Color(devSettings.directColor),
        devSettings.directIntensity
    );
    directionalLight.position.set(5, 10, 7.5);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.01;
    directionalLight.shadow.camera.far = 2; // Small value for AR-sized model
    directionalLight.shadow.bias = -0.0001;
    directionalLight.shadow.normalBias = 0.02;
    scene.add(directionalLight);

    // Load environment map (HDR)
    // const rgbeLoader = new RGBELoader();
    // rgbeLoader.load('klippad_sunrise_2_2k.hdr', (texture: THREE.DataTexture) => {
    //     texture.mapping = THREE.EquirectangularReflectionMapping;
    //     envMap = texture;
    //     scene.environment = envMap;
    //     scene.environmentIntensity = devSettings.envIntensity;
    //     // Note: skybox is set via CubeTextureLoader, not from envMap
    //     console.log('Environment map loaded: klippad_sunrise_2_2k.hdr');
    // });

    // Load GLTF model with Draco compression support
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        'glb/MiamiBeach.glb?v=' + Date.now(),
        (gltf) => {
            model = gltf.scene;

            // Center the model
            const box = new THREE.Box3().setFromObject(model);
            const center = box.getCenter(new THREE.Vector3());
            const size = box.getSize(new THREE.Vector3());

            model.position.x = -center.x;
            model.position.y = -box.min.y;
            model.position.z = -center.z;

            // Enable shadows, frustum culling, and find lights from GLB
            model.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    mesh.frustumCulled = true; // Enable frustum culling for performance

                    // Optimize geometry
                    if (mesh.geometry) {
                        mesh.geometry.computeBoundingSphere();
                        mesh.geometry.computeBoundingBox();
                    }

                    if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        mat.shadowSide = THREE.FrontSide;
                        if (mat.envMapIntensity !== undefined) {
                            mat.envMapIntensity = 1.0;
                        }
                    }
                }

                if ((child as THREE.Light).isLight) {
                    const light = child as THREE.Light;
                    console.log('Found light in GLB:', light.type, 'name:', light.name, 'intensity:', light.intensity);
                    glbLights.push(light);

                    if ((light as THREE.PointLight).isPointLight) {
                        const pointLight = light as THREE.PointLight;
                        glbPointLights.push(pointLight);
                        pointLight.castShadow = false; // Disable shadows for point lights
                        pointLight.decay = 2;
                        pointLight.distance = 0;
                    }

                    if ((light as THREE.SpotLight).isSpotLight) {
                        const spotLight = light as THREE.SpotLight;
                        glbSpotLights.push(spotLight);
                        spotLight.castShadow = false; // Disable shadows for spot lights
                        spotLight.penumbra = 0.5;
                        spotLight.decay = 2;
                    }

                    if ((light as THREE.DirectionalLight).isDirectionalLight) {
                        const dirLight = light as THREE.DirectionalLight;
                        dirLight.castShadow = true; // Only directional lights cast shadows
                        dirLight.shadow.mapSize.width = 2048;
                        dirLight.shadow.mapSize.height = 2048;
                        dirLight.shadow.camera.near = 0.01;
                        dirLight.shadow.camera.far = 2; // Small for AR model
                        dirLight.shadow.bias = -0.0001;
                        dirLight.shadow.normalBias = 0.02;
                        dirLight.shadow.camera.left = -50;
                        dirLight.shadow.camera.right = 50;
                        dirLight.shadow.camera.top = 50;
                        dirLight.shadow.camera.bottom = -50;
                    }
                }

                if ((child as THREE.Camera).isCamera) {
                    console.log('Found camera in GLB:', child.name, child.type);
                    glbCameras.push(child as THREE.Camera);
                }
            });

            // Scale up the model
            model.scale.set(5, 5, 5);
            scene.add(model);

            // Find water meshes in the model and apply ocean shader
            const waterMeshes: THREE.Mesh[] = [];
            model.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    const name = mesh.name.toLowerCase();
                    // Detect water by name or by checking if material is transparent/has water-like properties
                    if (name.includes('water') || name.includes('ocean') || name.includes('sea')) {
                        waterMeshes.push(mesh);
                        console.log('Found water mesh by name:', mesh.name);
                    } else if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        // Check for transparent turquoise/blue materials that might be water
                        if (mat.transparent && mat.opacity < 1) {
                            const color = mat.color;
                            // Check if it's a blue/turquoise color (water-like)
                            if (color && color.b > 0.3 && color.b > color.r) {
                                waterMeshes.push(mesh);
                                console.log('Found water mesh by material:', mesh.name, 'color:', color);
                            }
                        }
                    }
                }
            });

            // Load water normals texture
            const waterNormalsTexture = new THREE.TextureLoader().load('textures/waternormals.jpg', (texture) => {
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            });

            var plane = new THREE.PlaneGeometry(50, 50, 1, 1);

            waterNormalsTexture.generateMipmaps = true;
            waterNormalsTexture.minFilter = THREE.LinearMipmapLinearFilter;

            const maxAnisotropy = renderManager.renderer.capabilities.getMaxAnisotropy();
            waterNormalsTexture.anisotropy = maxAnisotropy;

            const wat = new Water(plane, {
                textureWidth: 512,
                textureHeight: 512,
                waterNormals: waterNormalsTexture,
                sunDirection: new THREE.Vector3(
                    waterSettings.sunDirectionX,
                    waterSettings.sunDirectionY,
                    waterSettings.sunDirectionZ
                ).normalize(),
                sunColor: parseInt(waterSettings.sunColor.replace('#', ''), 16),
                waterColor: parseInt(waterSettings.color.replace('#', ''), 16),
                distortionScale: waterSettings.distortionScale,
                fog: false
            });
            wat.name = "Ocean";
            wat.rotation.x = -Math.PI / 2;
            wat.position.set(0, 0.315, 0);
            wat.renderOrder = 1;
            const waterMat = wat.material as THREE.ShaderMaterial;
            waterMat.transparent = true;
            if (waterMat.uniforms['alpha']) {
                waterMat.uniforms['alpha'].value = waterSettings.alpha;
            }
            if (waterMat.uniforms['size']) {
                waterMat.uniforms['size'].value = waterSettings.size;
            }
            wat.scale.set(0.1, 0.05, 1);
            scene.add(wat);
            //
            // wat.reflectionCamera.layers.disableAll();
            // wat.reflectionCamera.layers.enable(1);

            oceanWater = wat;


            // Create ocean water shader material for each water mesh
            waterMeshes.forEach((waterMesh) => {
                // Create a Water object using the existing geometry
                waterMesh.geometry.computeVertexNormals();
                const water = new Water(waterMesh.geometry, {
                    textureWidth: 512,
                    textureHeight: 512,
                    waterNormals: waterNormalsTexture,
                    sunDirection: new THREE.Vector3(
                        waterSettings.sunDirectionX,
                        waterSettings.sunDirectionY,
                        waterSettings.sunDirectionZ
                    ).normalize(),
                    sunColor: parseInt(waterSettings.sunColor.replace('#', ''), 16),
                    waterColor: parseInt(waterSettings.color.replace('#', ''), 16),
                    distortionScale: waterSettings.distortionScale,
                    fog: false
                });

                // Copy transform from original mesh
                water.position.copy(waterMesh.position);
                water.rotation.copy(waterMesh.rotation);
                water.scale.copy(waterMesh.scale);

                // Set initial uniforms from waterSettings
                const waterMat = water.material as THREE.ShaderMaterial;
                waterMat.transparent = true;
                if (waterMat.uniforms['alpha']) {
                    waterMat.uniforms['alpha'].value = waterSettings.alpha;
                }
                if (waterMat.uniforms['size']) {
                    waterMat.uniforms['size'].value = waterSettings.size;
                }

                // Replace the mesh in the scene
                if (waterMesh.parent) {
                    // waterMesh.parent.add(water);
                    waterMesh.parent.remove(waterMesh);
                }

                // Store all water meshes for animation and GUI control
                allWaterMeshes.push(water);

                // Store first reference for backward compatibility
                if (!oceanWater) {
                    oceanWater = water;
                }

                console.log('Applied ocean shader to water mesh:', waterMesh.name);
            });

            if (waterMeshes.length === 0) {
                console.log('No water meshes found automatically. Listing all mesh names for debugging:');
                model.traverse((child: THREE.Object3D) => {
                    if ((child as THREE.Mesh).isMesh) {
                        console.log('  Mesh:', child.name);
                    }
                });
            }

            // Load Pinpoints model (with floating animation)
            loader.load(
                'glb/MiamiBeach_Pinpoints.glb?v=' + Date.now(),
                (gltf) => {
                    pinpointsModel = gltf.scene;
                    pinpointsModel.position.x = -center.x;
                    pinpointsModel.position.y = -box.min.y;
                    pinpointsModel.position.z = -center.z;
                    pinpointsModel.scale.set(5, 5, 5);

                    // Store original Y position for animation
                    pinpointsModel.userData.originalY = pinpointsModel.position.y;
                    pinpointsStartTime = performance.now();

                    // Log all mesh names for configuration and collect circular meshes
                    const circularMeshes: THREE.Mesh[] = [];
                    console.log('=== Pinpoints Model Mesh Names ===');
                    pinpointsModel.traverse((child: THREE.Object3D) => {
                        if ((child as THREE.Mesh).isMesh) {
                            const mesh = child as THREE.Mesh;
                            child.renderOrder = 2;
                            console.log('Pinpoint mesh:', mesh.name);
                            if (mesh.name == "HitBox") return;

                            mesh.castShadow = true;
                            mesh.receiveShadow = true;
                            mesh.frustumCulled = true;
                            if (mesh.geometry) {
                                mesh.geometry.computeBoundingSphere();
                                // mesh.geometry.computeBoundingBox();

                                const sphere = mesh.geometry.boundingSphere;
                                if (sphere && sphere.radius) {
                                    const hitBoxGeo = new THREE.SphereGeometry(sphere.radius * 1.5, 16, 16);
                                    const hitBoxMat = new THREE.MeshBasicMaterial({
                                        color: 0xff0000,
                                        visible: false // Invisible (but still raycastable!)
                                    });

                                    const hitBox = new THREE.Mesh(hitBoxGeo, hitBoxMat);
                                    hitBox.position.copy(sphere.center);
                                    hitBox.userData.parentPinpoint = mesh;
                                    hitBox.name = "HitBox";
                                    mesh.add(hitBox);
                                }
                            }

                            // Collect meshes that might be circular pinpoint faces
                            // Look for meshes with "circle", "face", "icon", or numbered pinpoints
                            const nameLower = mesh.name.toLowerCase();
                            if (nameLower.includes('circle') ||
                                nameLower.includes('face') ||
                                nameLower.includes('icon') ||
                                nameLower.includes('pinpoint') ||
                                nameLower.includes('pin')) {
                                circularMeshes.push(mesh);
                            }
                        }
                    });
                    console.log('=== End Pinpoints Mesh Names ===');
                    console.log('Found potential circular meshes:', circularMeshes.map(m => m.name));

                    // Apply configured images to pinpoints
                    // applyPinpointImages(pinpointsModel, circularMeshes);

                    scene.add(pinpointsModel);
                    console.log('Loaded Pinpoints model with floating animation');
                },
                undefined,
                (error) => console.warn('Could not load Pinpoints:', error)
            );

            // Load PublicTransport model (with toggle control)
            loader.load(
                'glb/MiamiBeach_PublicTransport.glb?v=' + Date.now(),
                (gltf) => {
                    transportModel = gltf.scene;
                    transportModel.position.x = -center.x;
                    transportModel.position.y = -box.min.y;
                    transportModel.position.z = -center.z;
                    transportModel.scale.set(5, 5, 5);
                    transportModel.visible = true; // Start visible

                    transportModel.traverse((child: THREE.Object3D) => {
                        if ((child as THREE.Mesh).isMesh) {
                            const mesh = child as THREE.Mesh;
                            mesh.castShadow = true;
                            mesh.receiveShadow = true;
                            mesh.frustumCulled = true;
                            if (mesh.geometry) {
                                mesh.geometry.computeBoundingSphere();
                                mesh.geometry.computeBoundingBox();
                            }
                        }
                    });

                    scene.add(transportModel);
                    console.log('Loaded PublicTransport model with toggle control');
                },
                undefined,
                (error) => console.warn('Could not load PublicTransport:', error)
            );

            // Load Choppers model separately for animation
            loader.load(
                'glb/MiamiBeach_Choppers.glb?v=' + Date.now(),
                (choppersGltf) => {
                    choppersModel = choppersGltf.scene;

                    // Match the main model's transformations
                    choppersModel.position.x = -center.x;
                    choppersModel.position.y = -box.min.y;
                    choppersModel.position.z = -center.z;
                    choppersModel.scale.set(5, 5, 5);

                    // Store original position for animation
                    choppersModel.userData.originalPosition = choppersModel.position.clone();
                    choppersModel.userData.originalY = choppersModel.position.y;
                    chopperStartTime = performance.now();

                    // Find rotors and enable shadows
                    chopperRotors = [];
                    choppersModel.traverse((child: THREE.Object3D) => {
                        const name = child.name.toLowerCase();
                        // Look for rotor parts by common naming conventions
                        if (name.includes('rotor') || name.includes('blade') || name.includes('prop') ||
                            name.includes('spinner') || name.includes('fan')) {
                            chopperRotors.push(child);
                            console.log('Found rotor:', child.name);
                        }

                        if ((child as THREE.Mesh).isMesh) {
                            const mesh = child as THREE.Mesh;
                            mesh.castShadow = true;
                            mesh.receiveShadow = false;
                            mesh.frustumCulled = true;
                        }
                    });

                    // If no rotors found by name, try to find spinning parts by geometry
                    if (chopperRotors.length === 0) {
                        console.log('No named rotors found, will rotate entire model children');
                        // Store all direct children that might be rotors
                        choppersModel.children.forEach((child, index) => {
                            if (child.children.length > 0) {
                                child.children.forEach(subChild => {
                                    chopperRotors.push(subChild);
                                });
                            }
                        });
                    }

                    scene.add(choppersModel);
                    console.log('Loaded Choppers model with', chopperRotors.length, 'potential rotor parts');
                },
                undefined,
                (error) => {
                    console.warn('Could not load Choppers model:', error);
                }
            );

            // Apply specific light settings
            applyLightSettings();

            // Recalculate size after scaling
            const scaledBox = new THREE.Box3().setFromObject(model);
            const scaledSize = scaledBox.getSize(new THREE.Vector3());

            console.log('Original model size:', size);
            console.log('Scaled model size:', scaledSize);
            console.log('Model center:', center);

            cameraManager.fitToModel(model);
            populateGLBControls();

            setTimeout(() => {
                cameraManager.flyToPoint(center, 3);
            }, 100);

            // Hide loading screen
            if (loadingScreen) {
                loadingScreen.style.opacity = '0';
                setTimeout(() => {
                    loadingScreen.style.display = 'none';
                }, 500);
            }

            // Start background preloading of apartments
            preloadApartments();

            // Create default hotspots
            createDefaultHotspots();

            // Update shadows once after model load (shadowMap.autoUpdate is false for performance)
            renderManager.renderer.shadowMap.needsUpdate = true;
        },
        (xhr) => {
            const percent = xhr.total > 0 ? Math.min(Math.round((xhr.loaded / xhr.total) * 100), 100) : 0;
            if (logoFillWrapper) {
                logoFillWrapper.style.clipPath = `inset(0 ${100 - percent}% 0 0)`;
            }
            if (progressText) progressText.textContent = percent + '%';
        },
        (error) => {
            console.error('Error loading model:', error);
            alert('Failed to load 3D model');
        }
    );

    window.addEventListener('resize', onWindowResize);
    // initDevGUI();
    animate(0);
}

// Populate GLB camera buttons and light controls
function populateGLBControls(): void {
    if (!window.guiFolders) return;

    glbCameras.forEach((glbCam, index) => {
        const camName = glbCam.name || `Camera ${index + 1}`;
        const flyToFunc = () => cameraManager.flyToCamera(glbCam);
        window.guiFolders.cameras.add({ [camName]: flyToFunc }, camName).name(`Fly to: ${camName}`);
    });

    glbPointLights.forEach((light, index) => {
        const lightName = light.name || `Point Light ${index + 1}`;
        const folder = window.guiFolders.pointLights.addFolder(lightName);

        const lightSettings = {
            enabled: light.visible,
            intensity: light.intensity,
            color: '#' + light.color.getHexString(),
            distance: light.distance,
            decay: light.decay
        };

        folder.add(lightSettings, 'enabled').name('Enabled').onChange((v: boolean) => {
            light.visible = v;
        });
        folder.add(lightSettings, 'intensity', 0, 100).name('Intensity').onChange((v: number) => {
            light.intensity = v;
        });
        folder.addColor(lightSettings, 'color').name('Color').onChange((v: string) => {
            light.color.set(v);
        });
        folder.add(lightSettings, 'distance', 0, 100).name('Distance').onChange((v: number) => {
            light.distance = v;
        });
        folder.add(lightSettings, 'decay', 0, 5).name('Decay').onChange((v: number) => {
            light.decay = v;
        });
    });

    glbSpotLights.forEach((light, index) => {
        const lightName = light.name || `Spot Light ${index + 1}`;
        const folder = window.guiFolders.spotLights.addFolder(lightName);

        const lightSettings = {
            enabled: light.visible,
            intensity: light.intensity,
            color: '#' + light.color.getHexString(),
            distance: light.distance,
            angle: THREE.MathUtils.radToDeg(light.angle),
            penumbra: light.penumbra,
            decay: light.decay
        };

        folder.add(lightSettings, 'enabled').name('Enabled').onChange((v: boolean) => {
            light.visible = v;
        });
        folder.add(lightSettings, 'intensity', 0, 100).name('Intensity').onChange((v: number) => {
            light.intensity = v;
        });
        folder.addColor(lightSettings, 'color').name('Color').onChange((v: string) => {
            light.color.set(v);
        });
        folder.add(lightSettings, 'distance', 0, 100).name('Distance').onChange((v: number) => {
            light.distance = v;
        });
        folder.add(lightSettings, 'angle', 0, 90).name('Angle (deg)').onChange((v: number) => {
            light.angle = THREE.MathUtils.degToRad(v);
        });
        folder.add(lightSettings, 'penumbra', 0, 1).name('Penumbra').onChange((v: number) => {
            light.penumbra = v;
        });
        folder.add(lightSettings, 'decay', 0, 5).name('Decay').onChange((v: number) => {
            light.decay = v;
        });
    });

    console.log(`Added ${glbCameras.length} camera buttons, ${glbPointLights.length} point lights, ${glbSpotLights.length} spot lights to GUI`);
}

// Apply specific light settings from user configuration
function applyLightSettings(): void {
    glbPointLights.forEach(light => {
        const name = light.name.toLowerCase();

        if (name.includes('purple')) {
            light.visible = true;
            light.intensity = 39;
            light.color.set('#4700ff');
            light.distance = 100;
            light.decay = 0;
            console.log('Applied purplelight settings:', light.name);
        } else if (name.includes('pink')) {
            light.visible = true;
            light.intensity = 7;
            light.color.set('#d400ff');
            light.distance = 0;
            light.decay = 0;
            console.log('Applied pinklight settings:', light.name);
        }
    });

    glbSpotLights.forEach(light => {
        const name = light.name.toLowerCase();

        if (name.includes('spot')) {
            light.visible = true;
            light.intensity = 24;
            light.color.set('#ffc4af');
            light.distance = 0;
            light.angle = THREE.MathUtils.degToRad(10);
            light.penumbra = 1;
            light.decay = 1.4;
            console.log('Applied spotlight settings:', light.name);
        }
    });

    console.log('Light settings applied');
}
// Initialize dev GUI
function initDevGUI(): void {
    gui = new dat.GUI({ width: 300 });
    gui.domElement.style.marginTop = '50px';
    gui.close(); // Close by default

    // Renderer folder
    const rendererFolder = gui.addFolder('Renderer');
    rendererFolder.add(devSettings, 'exposure', -5, 5).name('Exposure (EV)').onChange((v: number) => {
        renderManager.renderer.toneMappingExposure = Math.pow(2, v);
    });

    // Tone mapping options (postprocessing library modes)
    const toneMappingOptions: { [key: string]: ToneMappingMode } = {
        'Linear': ToneMappingMode.LINEAR,
        'Reinhard': ToneMappingMode.REINHARD,
        'Reinhard2': ToneMappingMode.REINHARD2,
        'Reinhard2 Adaptive': ToneMappingMode.REINHARD2_ADAPTIVE,
        'Uncharted2': ToneMappingMode.UNCHARTED2,
        'Optimized Cineon': ToneMappingMode.OPTIMIZED_CINEON,
        'ACES Filmic': ToneMappingMode.ACES_FILMIC,
        'AGX': ToneMappingMode.AGX,
        'Neutral': ToneMappingMode.NEUTRAL
    };
    rendererFolder.add(devSettings, 'toneMapping', Object.keys(toneMappingOptions)).name('Tone Mapping').onChange((v: string) => {
        renderManager.toneMappingEffect.mode = toneMappingOptions[v];
    });

    // Lighting folder
    const lightFolder = gui.addFolder('Lighting');
    lightFolder.add(devSettings, 'ambientIntensity', 0, 2).name('Ambient Intensity').onChange((v: number) => {
        if (ambientLight) ambientLight.intensity = v;
    });
    lightFolder.add(devSettings, 'directIntensity', 0, 5).name('Direct Intensity').onChange((v: number) => {
        scene.traverse((child: THREE.Object3D) => {
            if ((child as THREE.DirectionalLight).isDirectionalLight && child !== ambientLight) {
                (child as THREE.DirectionalLight).intensity = v;
            }
        });
    });
    lightFolder.add(devSettings, 'punctualLights').name('Punctual Lights').onChange((v: boolean) => {
        glbLights.forEach(light => {
            light.visible = v;
        });
    });

    // Environment folder
    const envFolder = gui.addFolder('Environment (HDR)');
    envFolder.add(devSettings, 'envIntensity', 0, 3).name('Intensity').onChange((v: number) => {
        if (scene) scene.environmentIntensity = v;
    });
    // envFolder.add(devSettings, 'showEnvBackground').name('Show as Background').onChange((v: boolean) => {
    //     if (scene && envMap) {
    //         scene.background = v ? envMap : new THREE.Color(0x191919);
    //     }
    // });

    // Post-processing folder
    const postFolder = gui.addFolder('Post-Processing');

    postFolder.add(devSettings, 'bloomEnabled').name('Bloom').onChange((v: boolean) => {
        renderManager.bloomEffect.blendMode.opacity.value = v ? 1 : 0;
    });
    postFolder.add(devSettings, 'bloomIntensity', 0, 3).name('Bloom Intensity').onChange((v: number) => {
        renderManager.bloomEffect.intensity = v;
    });
    postFolder.add(devSettings, 'bloomLuminanceThreshold', 0, 1).name('Bloom Threshold').onChange((v: number) => {
        renderManager.bloomEffect.luminanceMaterial.threshold = v;
    });
    postFolder.add(devSettings, 'bloomRadius', 0, 1).name('Bloom Radius').onChange((v: number) => {
        renderManager.bloomEffect.mipmapBlurPass.radius = v;
    });

    postFolder.add(devSettings, 'vignetteEnabled').name('Vignette').onChange((v: boolean) => {
        renderManager.vignetteEffect.blendMode.opacity.value = v ? 1 : 0;
    });
    postFolder.add(devSettings, 'vignetteOffset', 0, 1).name('Vignette Offset').onChange((v: number) => {
        renderManager.vignetteEffect.offset = v;
    });
    postFolder.add(devSettings, 'vignetteDarkness', 0, 1).name('Vignette Darkness').onChange((v: number) => {
        renderManager.vignetteEffect.darkness = v;
    });

    // SSAO Controls
    postFolder.add(devSettings, 'ssaoEnabled').name('SSAO').onChange((v: boolean) => {
        renderManager.ssaoEffect.blendMode.opacity.value = v ? 1 : 0;
    });
    postFolder.add(devSettings, 'ssaoIntensity', 0, 5).name('SSAO Intensity').onChange((v: number) => {
        renderManager.ssaoEffect.intensity = v;
    });
    postFolder.add(devSettings, 'ssaoRadius', 0.01, 1).name('SSAO Radius').onChange((v: number) => {
        renderManager.ssaoEffect.radius = v;
    });
    // Note: SSAO bias is set at initialization and cannot be changed dynamically
    postFolder.add(devSettings, 'ssaoBias', 0, 0.1).name('SSAO Bias (restart required)');

    // Model folder
    const modelFolder = gui.addFolder('Model');
    modelFolder.add(devSettings, 'modelScale', 0.1, 5).onChange((v: number) => {
        if (model) {
            model.scale.set(v, v, v);
        }
    });

    // Top View folder
    const topViewFolder = gui.addFolder('Top View');
    topViewFolder.add(devSettings, 'topViewZoom', 0.1, 5).name('Zoom Multiplier').onChange((v: number) => {
        console.log('Top View Zoom set to:', v);
    });

    // Actions
    const actionsFolder = gui.addFolder('Actions');
    actionsFolder.add(devSettings, 'fitToView').name('Fit to View');
    actionsFolder.add(devSettings, 'logCamera').name('Log Camera to Console');

    // Water Controls folder
    const waterFolder = gui.addFolder('Water');

    // Helper function to update all water meshes
    const updateWaterUniforms = () => {
        allWaterMeshes.forEach((water) => {
            const waterMat = water.material as THREE.ShaderMaterial;
            if (waterMat.uniforms) {
                // Update water color
                if (waterMat.uniforms['waterColor']) {
                    waterMat.uniforms['waterColor'].value.setHex(
                        parseInt(waterSettings.color.replace('#', ''), 16)
                    );
                }
                // Update sun color
                if (waterMat.uniforms['sunColor']) {
                    waterMat.uniforms['sunColor'].value.setHex(
                        parseInt(waterSettings.sunColor.replace('#', ''), 16)
                    );
                }
                // Update distortion
                if (waterMat.uniforms['distortionScale']) {
                    waterMat.uniforms['distortionScale'].value = waterSettings.distortionScale;
                }
                // Update alpha/transparency
                if (waterMat.uniforms['alpha']) {
                    waterMat.uniforms['alpha'].value = waterSettings.alpha;
                }
                // Update sun direction
                if (waterMat.uniforms['sunDirection']) {
                    waterMat.uniforms['sunDirection'].value.set(
                        waterSettings.sunDirectionX,
                        waterSettings.sunDirectionY,
                        waterSettings.sunDirectionZ
                    ).normalize();
                }
                // Update size (affects normal map scale)
                if (waterMat.uniforms['size']) {
                    waterMat.uniforms['size'].value = waterSettings.size;
                }
            }
        });
    };

    waterFolder.addColor(waterSettings, 'color').name('Water Color').onChange(updateWaterUniforms);
    waterFolder.addColor(waterSettings, 'sunColor').name('Sun Color').onChange(updateWaterUniforms);
    waterFolder.add(waterSettings, 'distortionScale', 0, 10).name('Wave Distortion').onChange(updateWaterUniforms);
    waterFolder.add(waterSettings, 'speed', 0, 5).name('Wave Speed');
    waterFolder.add(waterSettings, 'size', 0.1, 30).name('Wave Size').onChange(updateWaterUniforms);
    waterFolder.add(waterSettings, 'alpha', 0, 1).name('Transparency').onChange(updateWaterUniforms);
    waterFolder.add(waterSettings, 'sunDirectionX', -1, 1).name('Sun Dir X').onChange(updateWaterUniforms);
    waterFolder.add(waterSettings, 'sunDirectionY', -1, 1).name('Sun Dir Y').onChange(updateWaterUniforms);
    waterFolder.add(waterSettings, 'sunDirectionZ', -1, 1).name('Sun Dir Z').onChange(updateWaterUniforms);
    waterFolder.open(); // Open by default so you can see the controls

    // GLB Cameras folder
    const glbCameraFolder = gui.addFolder('GLB Cameras');

    // GLB Lights folders
    const pointLightsFolder = gui.addFolder('Point Lights');

    const spotLightsFolder = gui.addFolder('Spot Lights');

    window.guiFolders = {
        cameras: glbCameraFolder,
        pointLights: pointLightsFolder,
        spotLights: spotLightsFolder
    };
}

function onWindowResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    cameraManager.camera.aspect = width / height;
    cameraManager.camera.updateProjectionMatrix();
    renderManager.setSize(width, height);
}

// Hotspot functions
function createHotspot(
    position: THREE.Vector3,
    title: string = 'New Hotspot',
    description: string = '',
    apartmentIndex: number | null = null,
    mediaUrl: string = '',
    mediaType: 'image' | 'video' | null = null,
    color: string = defaultHotspotColor
): Hotspot {
    const id = 'hotspot-' + Date.now();

    // Create 3D marker - small sphere (scaled for the model size)
    const geometry = new THREE.SphereGeometry(0.15, 16, 16);
    const material = new THREE.MeshBasicMaterial({
        color: new THREE.Color(color),
        transparent: true,
        opacity: 0.95
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.copy(position);
    mesh.userData.hotspotId = id;
    scene.add(mesh);

    // Create HTML label with apartment indicator class if linked
    const hasApartmentClass = apartmentIndex !== null ? ' has-apartment' : '';
    const label = document.createElement('div');
    label.className = 'hotspot-label';
    label.innerHTML = `
        <div class="hotspot-marker${hasApartmentClass}" data-id="${id}" style="--hotspot-color: ${color};">
            <div class="hotspot-pulse"></div>
            <img src="/star.svg" alt="" class="hotspot-icon" />
        </div>
        <div class="hotspot-content" data-id="${id}">
            <div class="hotspot-title">${title}</div>
            <div class="hotspot-description">${description || 'Click to view'}</div>
        </div>
    `;
    label.dataset.id = id;
    hotspotContainer.appendChild(label);

    const hotspot: Hotspot = {
        id,
        position: position.clone(),
        title,
        description,
        mesh,
        label,
        apartmentIndex,
        mediaUrl,
        mediaType,
        color
    };

    hotspots.push(hotspot);
    window.hotspots = hotspots;

    // Add click handler for editing or viewing apartment
    label.addEventListener('click', (e) => {
        e.stopPropagation();
        if (hotspotEditMode) {
            openHotspotEditor(hotspot);
        } else {
            // If linked to apartment, open popup; otherwise toggle content
            if (hotspot.apartmentIndex !== null && hotspot.apartmentIndex > 0) {
                openApartmentPopup(hotspot);
            } else {
                toggleHotspotContent(hotspot);
            }
        }
    });

    console.log('Hotspot created:', id, position, apartmentIndex ? `linked to apartment ${apartmentIndex}` : '');
    return hotspot;
}

function toggleHotspotContent(hotspot: Hotspot): void {
    const content = hotspot.label.querySelector('.hotspot-content') as HTMLElement;
    if (content) {
        content.classList.toggle('visible');
    }
}

function openHotspotEditor(hotspot: Hotspot): void {
    selectedHotspot = hotspot;
    const modal = document.getElementById('hotspot-editor-modal');
    const titleInput = document.getElementById('hotspot-title-input') as HTMLInputElement;
    const descInput = document.getElementById('hotspot-desc-input') as HTMLTextAreaElement;
    const apartmentSelect = document.getElementById('hotspot-apartment-select') as HTMLSelectElement;
    const mediaTypeSelect = document.getElementById('hotspot-media-type') as HTMLSelectElement;
    const mediaUrlInput = document.getElementById('hotspot-media-url') as HTMLInputElement;
    const mediaUrlGroup = document.getElementById('hotspot-media-url-group');
    const colorInput = document.getElementById('hotspot-color-input') as HTMLInputElement;

    if (modal && titleInput && descInput) {
        titleInput.value = hotspot.title;
        descInput.value = hotspot.description;

        // Populate apartment selector
        if (apartmentSelect) {
            apartmentSelect.value = hotspot.apartmentIndex !== null ? String(hotspot.apartmentIndex) : '';
        }

        // Populate media fields
        if (mediaTypeSelect) {
            mediaTypeSelect.value = hotspot.mediaType || '';
        }
        if (mediaUrlInput) {
            mediaUrlInput.value = hotspot.mediaUrl || '';
        }
        // Show/hide media URL field based on media type
        if (mediaUrlGroup) {
            mediaUrlGroup.style.display = hotspot.mediaType ? 'block' : 'none';
        }

        // Populate color picker
        if (colorInput) {
            colorInput.value = hotspot.color;
        }

        modal.classList.add('active');
    }
}

// Create default hotspots on load (currently disabled)
function createDefaultHotspots(): void {
    // No default hotspots - users can add via edit mode
    console.log('Default hotspots disabled');
}

function updateHotspot(
    hotspot: Hotspot,
    title: string,
    description: string,
    apartmentIndex: number | null = null,
    mediaUrl: string = '',
    mediaType: 'image' | 'video' | null = null,
    color: string = hotspot.color
): void {
    hotspot.title = title;
    hotspot.description = description;
    hotspot.apartmentIndex = apartmentIndex;
    hotspot.mediaUrl = mediaUrl;
    hotspot.mediaType = mediaType;
    hotspot.color = color;

    const titleEl = hotspot.label.querySelector('.hotspot-title');
    const descEl = hotspot.label.querySelector('.hotspot-description');
    const marker = hotspot.label.querySelector('.hotspot-marker') as HTMLElement;

    if (titleEl) titleEl.textContent = title;
    if (descEl) descEl.textContent = description || 'Click to view';

    // Update marker visual indicator for apartment link and color
    if (marker) {
        marker.classList.toggle('has-apartment', apartmentIndex !== null);
        marker.style.setProperty('--hotspot-color', color);
    }

    // Update 3D mesh color
    const meshMaterial = hotspot.mesh.material as THREE.MeshBasicMaterial;
    meshMaterial.color.set(color);
}

function deleteHotspot(hotspot: Hotspot): void {
    scene.remove(hotspot.mesh);
    hotspot.label.remove();
    hotspots = hotspots.filter(h => h.id !== hotspot.id);
    window.hotspots = hotspots;
    console.log('Hotspot deleted:', hotspot.id);
}

function updateHotspotLabels(): void {
    hotspots.forEach(hotspot => {
        const screenPos = hotspot.position.clone().project(cameraManager.camera);

        // Check if hotspot is in front of camera
        const isBehind = screenPos.z > 1;

        if (isBehind) {
            hotspot.label.style.display = 'none';
            return;
        }

        hotspot.label.style.display = 'block';

        const x = (screenPos.x * 0.5 + 0.5) * window.innerWidth;
        const y = (-screenPos.y * 0.5 + 0.5) * window.innerHeight;

        hotspot.label.style.left = `${x}px`;
        hotspot.label.style.top = `${y}px`;

        // Scale based on distance
        const distance = cameraManager.camera.position.distanceTo(hotspot.position);
        const scale = Math.max(0.5, Math.min(1.5, 50 / distance));
        hotspot.label.style.transform = `translate(-50%, -50%) scale(${scale})`;
    });
}

function toggleHotspotEditMode(): void {
    hotspotEditMode = !hotspotEditMode;
    window.hotspotEditMode = hotspotEditMode;

    const editBtn = document.getElementById('hotspot-edit-btn');
    const indicator = document.getElementById('edit-mode-indicator');

    if (editBtn) {
        editBtn.classList.toggle('active', hotspotEditMode);
    }
    if (indicator) {
        indicator.classList.toggle('visible', hotspotEditMode);
    }

    // Update cursor
    if (renderManager.renderer) {
        renderManager.renderer.domElement.style.cursor = hotspotEditMode ? 'crosshair' : 'grab';
    }

    console.log('Hotspot edit mode:', hotspotEditMode ? 'ON' : 'OFF');
}

function exportHotspots(): string {
    const data = hotspots.map(h => ({
        id: h.id,
        position: { x: h.position.x, y: h.position.y, z: h.position.z },
        title: h.title,
        description: h.description,
        apartmentIndex: h.apartmentIndex,
        mediaUrl: h.mediaUrl,
        mediaType: h.mediaType,
        color: h.color
    }));
    return JSON.stringify(data, null, 2);
}

function importHotspots(jsonData: string): void {
    try {
        const data = JSON.parse(jsonData);
        data.forEach((item: any) => {
            const pos = new THREE.Vector3(item.position.x, item.position.y, item.position.z);
            createHotspot(
                pos,
                item.title,
                item.description,
                item.apartmentIndex ?? null,
                item.mediaUrl ?? '',
                item.mediaType ?? null,
                item.color ?? defaultHotspotColor
            );
        });
        console.log('Imported', data.length, 'hotspots');
    } catch (e) {
        console.error('Failed to import hotspots:', e);
    }
}

// Apartment model functions
function animateModelOut(targetModel: THREE.Group, onComplete: () => void): void {
    const duration = 800;
    const startTime = performance.now();
    const startScale = targetModel.scale.clone();
    const startOpacity = 1;

    // Make materials transparent for fade
    targetModel.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            if (mesh.material) {
                const mat = mesh.material as THREE.MeshStandardMaterial;
                mat.transparent = true;
            }
        }
    });

    function animateOut(currentTime: number): void {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);

        // Scale down and fade out
        const scale = startScale.x * (1 - eased * 0.3);
        targetModel.scale.set(scale, scale, scale);

        // Fade opacity
        const opacity = startOpacity * (1 - eased);
        targetModel.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    const mat = mesh.material as THREE.MeshStandardMaterial;
                    mat.opacity = opacity;
                }
            }
        });

        if (progress < 1) {
            requestAnimationFrame(animateOut);
        } else {
            targetModel.visible = false;
            onComplete();
        }
    }

    requestAnimationFrame(animateOut);
}

function animateModelIn(targetModel: THREE.Group, targetScale: number): void {
    const duration = 800;
    const startTime = performance.now();
    targetModel.visible = true;
    targetModel.scale.set(0.1, 0.1, 0.1);

    // Set initial opacity to 0
    targetModel.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) {
            const mesh = child as THREE.Mesh;
            if (mesh.material) {
                const mat = mesh.material as THREE.MeshStandardMaterial;
                mat.transparent = true;
                mat.opacity = 0;
            }
        }
    });

    function animateIn(currentTime: number): void {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out back for bouncy effect
        const c1 = 1.70158;
        const c3 = c1 + 1;
        const eased = 1 + c3 * Math.pow(progress - 1, 3) + c1 * Math.pow(progress - 1, 2);

        // Scale up
        const scale = targetScale * eased;
        targetModel.scale.set(scale, scale, scale);

        // Fade in opacity
        const opacity = Math.min(progress * 1.5, 1);
        targetModel.traverse((child: THREE.Object3D) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    const mat = mesh.material as THREE.MeshStandardMaterial;
                    mat.opacity = opacity;
                }
            }
        });

        if (progress < 1) {
            requestAnimationFrame(animateIn);
        } else {
            // Reset opacity to 1 and disable transparency if not needed
            targetModel.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        mat.opacity = 1;
                    }
                }
            });
            isTransitioning = false;
            updateApartmentLoadingState(false);
        }
    }

    requestAnimationFrame(animateIn);
}

function loadApartmentModel(apartment: ApartmentModel): void {
    if (isTransitioning || !apartment.file) return;

    isTransitioning = true;
    updateApartmentLoadingState(true);

    // If apartment is already loaded, just show it
    if (apartment.loaded && apartment.model) {
        showApartmentModel(apartment);
        return;
    }

    // Load the apartment model
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        apartment.file,
        (gltf) => {
            const aptModel = gltf.scene;

            // Center the apartment model
            const box = new THREE.Box3().setFromObject(aptModel);
            const center = box.getCenter(new THREE.Vector3());

            aptModel.position.x = -center.x;
            aptModel.position.y = -box.min.y;
            aptModel.position.z = -center.z;

            // Enable shadows and apply environment
            aptModel.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        mat.shadowSide = THREE.FrontSide;
                        if (mat.envMapIntensity !== undefined) {
                            mat.envMapIntensity = 1.0;
                        }
                    }
                }
            });

            // Scale to match main model (apartments might need different scaling)
            aptModel.scale.set(5, 5, 5);
            aptModel.visible = false;

            apartment.model = aptModel;
            apartment.loaded = true;

            scene.add(aptModel);
            console.log('Apartment model loaded:', apartment.name);

            showApartmentModel(apartment);
        },
        (xhr) => {
            const percent = xhr.total > 0 ? Math.min(Math.round((xhr.loaded / xhr.total) * 100), 100) : 0;
            console.log(`Loading ${apartment.name}: ${percent}%`);
        },
        (error) => {
            console.error('Error loading apartment model:', error);
            isTransitioning = false;
            updateApartmentLoadingState(false);
        }
    );
}

function showApartmentModel(apartment: ApartmentModel): void {
    // Hide current model (main or apartment) with animation
    if (currentApartment && currentApartment.model) {
        animateModelOut(currentApartment.model, () => {
            if (apartment.model) {
                animateModelIn(apartment.model, 5);
            }
        });
    } else if (model && model.visible) {
        animateModelOut(model, () => {
            if (apartment.model) {
                animateModelIn(apartment.model, 5);
            }
        });
    } else if (apartment.model) {
        animateModelIn(apartment.model, 5);
    }

    currentApartment = apartment;
}

function showMainModel(): void {
    if (isTransitioning) return;

    isTransitioning = true;
    updateApartmentLoadingState(true);

    // Hide current apartment with animation
    if (currentApartment && currentApartment.model) {
        animateModelOut(currentApartment.model, () => {
            // Show main model
            model.visible = true;
            animateModelIn(model, 5);
        });
    } else {
        model.visible = true;
        animateModelIn(model, 5);
    }

    currentApartment = null;
}

function switchApartment(index: number): void {
    const apartment = apartmentModels[index];
    if (!apartment) return;

    console.log('Switching to:', apartment.name);

    if (index === 0) {
        // Switch back to main model
        showMainModel();
    } else {
        loadApartmentModel(apartment);
    }
}

function updateApartmentLoadingState(loading: boolean): void {
    const selector = document.getElementById('apartment-selector') as HTMLSelectElement;
    const loadingIndicator = document.getElementById('apartment-loading');

    if (selector) {
        selector.disabled = loading;
    }
    if (loadingIndicator) {
        loadingIndicator.style.display = loading ? 'block' : 'none';
    }
}

// ============================================
// Apartment Popup System
// ============================================


let apartmentPopupAnimationId: number | null = null;
let preloadedApartmentModels: Map<number, THREE.Group> = new Map();

function initApartmentPopupRenderer(): void {
    const container = document.getElementById('apartment-popup-canvas');
    if (!container) return;

    // Create scene
    apartmentPopupScene = new THREE.Scene();
    apartmentPopupScene.background = new THREE.Color(0x191919);

    // Create camera - closer initial position for larger model view
    apartmentPopupCamera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 1000);
    apartmentPopupCamera.position.set(8, 5, 8);

    // Create renderer
    apartmentPopupRenderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
        powerPreference: 'high-performance'
    });
    apartmentPopupRenderer.setSize(container.clientWidth, container.clientHeight);
    apartmentPopupRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    // apartmentPopupRenderer.toneMapping = THREE.LinearToneMapping;
    apartmentPopupRenderer.toneMappingExposure = Math.pow(2, -2);
    apartmentPopupRenderer.shadowMap.enabled = true;
    apartmentPopupRenderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(apartmentPopupRenderer.domElement);

    // Create controls - allow closer zoom for larger view
    apartmentPopupControls = new OrbitControls(apartmentPopupCamera, apartmentPopupRenderer.domElement);
    apartmentPopupControls.enableDamping = true;
    apartmentPopupControls.dampingFactor = 0.05;
    apartmentPopupControls.minDistance = 2;
    apartmentPopupControls.maxDistance = 50;
    apartmentPopupControls.maxPolarAngle = Math.PI / 2;
    apartmentPopupControls.target.set(0, 2, 0);

    // Add lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    apartmentPopupScene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 2);
    directionalLight.position.set(10, 20, 10);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 2048;
    directionalLight.shadow.mapSize.height = 2048;
    directionalLight.shadow.camera.near = 0.01;
    directionalLight.shadow.camera.far = 2; // Small for AR model
    apartmentPopupScene.add(directionalLight);

    // Setup post-processing (lighter than main scene) using postprocessing library
    apartmentPopupComposer = new EffectComposer(apartmentPopupRenderer);

    // Create effects
    const popupBloomEffect = new BloomEffect({
        intensity: 0.3,
        luminanceThreshold: 0.85,
        luminanceSmoothing: 0.4
    });

    const popupToneMappingEffect = new ToneMappingEffect({
        mode: ToneMappingMode.LINEAR
    });

    // Add passes
    apartmentPopupComposer.addPass(new RenderPass(apartmentPopupScene, apartmentPopupCamera));
    apartmentPopupComposer.addPass(new EffectPass(apartmentPopupCamera, popupBloomEffect, popupToneMappingEffect));

    console.log('Apartment popup renderer initialized');
}

function loadApartmentForPopup(apartmentIndex: number, callback: () => void): void {
    if (!apartmentPopupScene) return;

    const loadingEl = document.getElementById('apartment-popup-loading');
    if (loadingEl) loadingEl.classList.add('active');

    // Check if already preloaded
    if (preloadedApartmentModels.has(apartmentIndex)) {
        const cachedModel = preloadedApartmentModels.get(apartmentIndex)!;
        const clonedModel = cachedModel.clone();
        clonedModel.visible = true;
        apartmentPopupScene.add(clonedModel);
        if (loadingEl) loadingEl.classList.remove('active');
        console.log('Using preloaded apartment model:', apartmentIndex);
        // Fit camera to model for 80% fill
        fitApartmentPopupCamera(clonedModel);
        callback();
        return;
    }

    // Get apartment data
    const apartment = apartmentModels[apartmentIndex];
    if (!apartment || !apartment.file) {
        console.error('Invalid apartment index:', apartmentIndex);
        if (loadingEl) loadingEl.classList.remove('active');
        return;
    }

    // Load model
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);

    loader.load(
        apartment.file,
        (gltf) => {
            const aptModel = gltf.scene;

            // Center the model
            const box = new THREE.Box3().setFromObject(aptModel);
            const center = box.getCenter(new THREE.Vector3());
            aptModel.position.x = -center.x;
            aptModel.position.y = -box.min.y;
            aptModel.position.z = -center.z;

            // Apply environment and shadows
            aptModel.traverse((child: THREE.Object3D) => {
                if ((child as THREE.Mesh).isMesh) {
                    const mesh = child as THREE.Mesh;
                    mesh.castShadow = true;
                    mesh.receiveShadow = true;
                    if (mesh.material) {
                        const mat = mesh.material as THREE.MeshStandardMaterial;
                        if (mat.envMapIntensity !== undefined) {
                            mat.envMapIntensity = 1.0;
                        }
                    }
                }
            });

            aptModel.scale.set(5, 5, 5);
            apartmentPopupScene!.add(aptModel);

            if (loadingEl) loadingEl.classList.remove('active');
            console.log('Apartment model loaded for popup:', apartment.name);
            // Fit camera to model for 80% fill
            fitApartmentPopupCamera(aptModel);
            callback();
        },
        (xhr) => {
            const percent = xhr.total > 0 ? Math.min(Math.round((xhr.loaded / xhr.total) * 100), 100) : 0;
            console.log(`Loading popup apartment: ${percent}%`);
        },
        (error) => {
            console.error('Error loading apartment for popup:', error);
            if (loadingEl) loadingEl.classList.remove('active');
        }
    );
}

// Fit camera to apartment model so it fills ~80% of the view
function fitApartmentPopupCamera(model: THREE.Object3D): void {
    if (!apartmentPopupCamera || !apartmentPopupControls) return;

    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    // Get the max dimension for fitting
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = apartmentPopupCamera.fov * (Math.PI / 180);

    // Calculate distance to fill ~80% of view (0.8 factor means closer)
    let cameraDistance = maxDim / (2 * Math.tan(fov / 2));
    cameraDistance *= 0.6; // Bring camera closer to fill 80%

    // Position camera at an angle for nice 3D view
    const angle = Math.PI / 4; // 45 degrees
    apartmentPopupCamera.position.set(
        center.x + cameraDistance * Math.sin(angle),
        center.y + cameraDistance * 0.5,
        center.z + cameraDistance * Math.cos(angle)
    );

    // Update controls target to model center
    apartmentPopupControls.target.set(center.x, center.y, center.z);
    apartmentPopupControls.update();

    console.log('Apartment camera fitted to model, distance:', cameraDistance);
}

function animateApartmentPopup(): void {
    if (!apartmentPopupActive) return;

    apartmentPopupAnimationId = requestAnimationFrame(animateApartmentPopup);

    if (apartmentPopupControls) {
        apartmentPopupControls.update();
    }

    if (apartmentPopupComposer) {
        apartmentPopupComposer.render();
    }
}

function openApartmentPopup(hotspot: Hotspot): void {
    if (!hotspot.apartmentIndex) return;

    currentPopupHotspot = hotspot;
    apartmentPopupActive = true;
    mainRenderPaused = true;

    // Hide main model for performance
    if (model) model.visible = false;
    if (currentApartment && currentApartment.model) {
        currentApartment.model.visible = false;
    }

    // Show popup modal
    const modal = document.getElementById('apartment-popup-modal');
    if (modal) {
        modal.classList.add('active');
    }

    // Update popup info
    const titleEl = document.getElementById('apartment-popup-title');
    const descEl = document.getElementById('apartment-popup-description');
    const mediaContainer = document.getElementById('apartment-popup-media');

    if (titleEl) titleEl.textContent = hotspot.title;
    if (descEl) descEl.textContent = hotspot.description || 'Explore this stunning Miami Beach residence in 3D.';

    // Handle media
    if (mediaContainer) {
        mediaContainer.innerHTML = '';
        mediaContainer.classList.remove('has-media');

        if (hotspot.mediaType && hotspot.mediaUrl) {
            mediaContainer.classList.add('has-media');
            if (hotspot.mediaType === 'image') {
                const img = document.createElement('img');
                img.src = hotspot.mediaUrl;
                img.alt = hotspot.title;
                mediaContainer.appendChild(img);
            } else if (hotspot.mediaType === 'video') {
                const video = document.createElement('video');
                video.src = hotspot.mediaUrl;
                video.controls = true;
                video.autoplay = false;
                mediaContainer.appendChild(video);
            }
        }
    }

    // Initialize renderer if not done
    if (!apartmentPopupRenderer) {
        initApartmentPopupRenderer();
    }

    // Load apartment model
    loadApartmentForPopup(hotspot.apartmentIndex, () => {
        // Start animation loop
        animateApartmentPopup();
    });

    console.log('Opened apartment popup for:', hotspot.title);
}

function closeApartmentPopup(): void {
    apartmentPopupActive = false;
    mainRenderPaused = false;
    currentPopupHotspot = null;

    // Stop animation loop
    if (apartmentPopupAnimationId) {
        cancelAnimationFrame(apartmentPopupAnimationId);
        apartmentPopupAnimationId = null;
    }

    // Hide popup modal
    const modal = document.getElementById('apartment-popup-modal');
    if (modal) {
        modal.classList.remove('active');
    }

    // Clear popup scene (remove models, keep lights)
    if (apartmentPopupScene) {
        const toRemove: THREE.Object3D[] = [];
        apartmentPopupScene.traverse((child) => {
            if (child instanceof THREE.Group || (child as THREE.Mesh).isMesh) {
                // Don't remove lights
                if (!(child instanceof THREE.Light)) {
                    toRemove.push(child);
                }
            }
        });
        toRemove.forEach((obj) => {
            if (obj.parent === apartmentPopupScene) {
                apartmentPopupScene!.remove(obj);
                // Dispose geometry and materials
                if ((obj as THREE.Mesh).geometry) {
                    (obj as THREE.Mesh).geometry.dispose();
                }
                if ((obj as THREE.Mesh).material) {
                    const mat = (obj as THREE.Mesh).material;
                    if (Array.isArray(mat)) {
                        mat.forEach(m => m.dispose());
                    } else {
                        mat.dispose();
                    }
                }
            }
        });
    }

    // Show main model again
    if (currentApartment && currentApartment.model) {
        currentApartment.model.visible = true;
    } else if (model) {
        model.visible = true;
    }

    console.log('Closed apartment popup');
}

function preloadApartments(): void {
    console.log('Starting background apartment preloading...');

    const loadNext = (index: number) => {
        if (index >= apartmentModels.length) {
            console.log('All apartments preloaded');
            return;
        }

        const apartment = apartmentModels[index];
        if (!apartment.file || index === 0) {
            // Skip main model (index 0) and invalid entries
            loadNext(index + 1);
            return;
        }

        // Use requestIdleCallback for non-blocking loading
        const loadFunc = () => {
            const dracoLoader = new DRACOLoader();
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

            const loader = new GLTFLoader();
            loader.setDRACOLoader(dracoLoader);

            loader.load(
                apartment.file!,
                (gltf) => {
                    const aptModel = gltf.scene;

                    // Center the model
                    const box = new THREE.Box3().setFromObject(aptModel);
                    const center = box.getCenter(new THREE.Vector3());
                    aptModel.position.x = -center.x;
                    aptModel.position.y = -box.min.y;
                    aptModel.position.z = -center.z;

                    // Apply settings
                    aptModel.traverse((child: THREE.Object3D) => {
                        if ((child as THREE.Mesh).isMesh) {
                            const mesh = child as THREE.Mesh;
                            mesh.castShadow = true;
                            mesh.receiveShadow = true;
                        }
                    });

                    aptModel.scale.set(5, 5, 5);
                    aptModel.visible = false;

                    preloadedApartmentModels.set(index, aptModel);
                    console.log('Preloaded apartment:', apartment.name);

                    // Load next after delay
                    setTimeout(() => loadNext(index + 1), 1000);
                },
                undefined,
                (error) => {
                    console.warn('Failed to preload apartment:', apartment.name, error);
                    setTimeout(() => loadNext(index + 1), 500);
                }
            );
        };

        if ('requestIdleCallback' in window) {
            (window as any).requestIdleCallback(loadFunc, { timeout: 5000 });
        } else {
            setTimeout(loadFunc, 100);
        }
    };

    // Start preloading after 3 seconds
    setTimeout(() => loadNext(1), 3000);
}

// Handle popup resize
function handlePopupResize(): void {
    if (!apartmentPopupActive || !apartmentPopupRenderer || !apartmentPopupCamera || !apartmentPopupComposer) return;

    const container = document.getElementById('apartment-popup-canvas');
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    apartmentPopupCamera.aspect = width / height;
    apartmentPopupCamera.updateProjectionMatrix();

    apartmentPopupRenderer.setSize(width, height);
    apartmentPopupComposer.setSize(width, height);
}

window.addEventListener('resize', handlePopupResize);

// Frame limiting for performance
let lastFrameTime = 0;
const targetFPS = 60;
const frameInterval = 1000 / targetFPS;

function animate(time: number): void {
    requestAnimationFrame(animate);

    // Skip rendering when apartment popup is active
    if (mainRenderPaused) return;

    // Frame rate limiting
    const deltaTime = time - lastFrameTime;
    if (deltaTime < frameInterval) return;
    lastFrameTime = time - (deltaTime % frameInterval);

    cameraManager.update();
    popup3D.update();

    // Animate helicopters - realistic flight with spinning rotors
    if (choppersModel && choppersModel.userData.originalPosition) {
        const elapsed = (time - chopperStartTime) / 1000; // seconds
        const deltaSeconds = deltaTime / 1000;

        // Spin all rotors fast (Z axis for horizontal rotation viewed from above)
        chopperRotors.forEach((rotor) => {
            rotor.rotation.z += chopperConfig.rotorSpeed * deltaSeconds;
        });

        // More natural figure-8 / lemniscate path instead of simple circle
        const t = elapsed * chopperConfig.circleSpeed;

        // Lemniscate (figure-8) creates more interesting flight pattern
        const scale = chopperConfig.circleRadius;
        const pathX = scale * Math.cos(t);
        const pathZ = scale * Math.sin(t) * Math.cos(t); // Figure-8 pattern

        // Add subtle Perlin-like noise for natural drift (using sin waves at different frequencies)
        const drift1 = Math.sin(elapsed * 0.7) * chopperConfig.driftAmount * 0.3;
        const drift2 = Math.sin(elapsed * 1.3) * chopperConfig.driftAmount * 0.2;
        const drift3 = Math.cos(elapsed * 0.5) * chopperConfig.driftAmount * 0.25;

        // Gentle altitude variation
        const altitudeWave = Math.sin(elapsed * 0.4) * chopperConfig.altitudeVariation;
        const hoverBob = Math.sin(elapsed * chopperConfig.hoverSpeed) * chopperConfig.hoverHeight;

        // Apply position with smooth natural movement
        const origPos = choppersModel.userData.originalPosition;
        choppersModel.position.x = origPos.x + pathX + drift1;
        choppersModel.position.y = choppersModel.userData.originalY + 0.4 + hoverBob + altitudeWave;
        choppersModel.position.z = origPos.z + pathZ + drift2 + drift3;

        // Calculate velocity direction for natural heading
        const velocityX = -scale * Math.sin(t) * chopperConfig.circleSpeed;
        const velocityZ = scale * (Math.cos(t) * Math.cos(t) - Math.sin(t) * Math.sin(t)) * chopperConfig.circleSpeed;

        // Face direction of travel smoothly
        const targetHeading = Math.atan2(velocityX, velocityZ);
        choppersModel.rotation.y = targetHeading;

        // Bank into turns based on turn rate (derivative of heading)
        const turnRate = Math.abs(velocityX) / (Math.abs(velocityX) + Math.abs(velocityZ) + 0.01);
        const bankDirection = velocityX > 0 ? 1 : -1;
        choppersModel.rotation.z = bankDirection * turnRate * chopperConfig.tiltAmount;

        // Slight pitch based on altitude change
        choppersModel.rotation.x = -Math.cos(elapsed * 0.4) * 0.02;
    }

    // Animate pinpoints - subtle floating up and down
    if (pinpointsModel && pinpointsModel.userData.originalY !== undefined) {
        const elapsed = (time - pinpointsStartTime) / 1000;
        // Gentle floating: 0.02 units amplitude, slow speed
        const floatOffset = Math.sin(elapsed * 0.8) * 0.02;
        pinpointsModel.position.y = pinpointsModel.userData.originalY + floatOffset;
    }

    if (oceanWater) {
        oceanWater.material.uniforms['time'].value += (1.0 / 60.0) * waterSettings.speed;
    }

    // Animate ocean water waves (all water meshes)
    allWaterMeshes.forEach((water) => {
        const waterMat = water.material as THREE.ShaderMaterial;
        if (waterMat.uniforms['time']) {
            waterMat.uniforms['time'].value += (1.0 / 60.0) * waterSettings.speed;
        }
    });

    // Update hotspot label positions (throttled - every other frame)
    if (Math.floor(time / 32) % 2 === 0) {
        updateHotspotLabels();
    }

    renderManager.render();
}

// Start Three.js when page loads
window.addEventListener('load', initThreeJS);

// ==========================================
// WEATHER WIDGET - Open-Meteo API (Free, no key required)
// ==========================================

async function fetchWeather(): Promise<void> {
    // Miami Beach coordinates
    const lat = 25.7907;
    const lon = -80.1300;

    try {
        // Open-Meteo free API - no API key needed
        const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&daily=temperature_2m_max&current_weather=true&temperature_unit=fahrenheit&timezone=America/New_York&forecast_days=3`;

        const response = await fetch(url);
        if (!response.ok) throw new Error('Weather fetch failed');

        const data = await response.json();

        // Update current temperature
        const currentTemp = Math.round(data.current_weather.temperature);
        const tempEl = document.getElementById('weather-temp');
        if (tempEl) tempEl.textContent = `${currentTemp}°F`;

        // Update forecast
        const dailyTemps = data.daily.temperature_2m_max;
        const dailyDates = data.daily.time;

        // Today
        const todayEl = document.getElementById('forecast-today');
        if (todayEl && dailyTemps[0] !== undefined) {
            todayEl.textContent = `${Math.round(dailyTemps[0])}°`;
        }

        // Tomorrow
        const tomorrowEl = document.getElementById('forecast-tomorrow');
        if (tomorrowEl && dailyTemps[1] !== undefined) {
            tomorrowEl.textContent = `${Math.round(dailyTemps[1])}°`;
        }

        // Day 3
        const day3El = document.getElementById('forecast-day3');
        const day3LabelEl = document.getElementById('forecast-day3-label');
        if (day3El && dailyTemps[2] !== undefined) {
            day3El.textContent = `${Math.round(dailyTemps[2])}°`;
        }
        if (day3LabelEl && dailyDates[2]) {
            const date = new Date(dailyDates[2]);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
            day3LabelEl.textContent = dayName;
        }

        console.log('Weather data loaded:', currentTemp + '°F');
    } catch (error) {
        console.warn('Could not fetch weather data:', error);
        // Keep default "--" values on error
    }
}

// Fetch weather on load
fetchWeather();

// UI Controls
document.addEventListener('DOMContentLoaded', () => {
    const menuBtn = document.getElementById('menu-btn');
    const mobileMenu = document.getElementById('mobile-menu');
    if (menuBtn && mobileMenu) {
        menuBtn.addEventListener('click', () => {
            mobileMenu.classList.toggle('active');
            menuBtn.classList.toggle('active');
        });
    }

    const settingsBtn = document.getElementById('settings-btn');
    const settingsPanel = document.getElementById('settings-panel');
    if (settingsBtn && settingsPanel) {
        settingsBtn.addEventListener('click', () => {
            settingsPanel.classList.toggle('active');
        });
    }

    const registerBtn = document.getElementById('register-btn');
    const registerModal = document.getElementById('register-modal');
    const modalClose = document.getElementById('modal-close');

    if (registerBtn && registerModal) {
        registerBtn.addEventListener('click', (e) => {
            e.preventDefault();
            registerModal.classList.add('active');
        });
    }

    if (modalClose && registerModal) {
        modalClose.addEventListener('click', () => {
            registerModal.classList.remove('active');
        });
    }

    if (registerModal) {
        registerModal.addEventListener('click', (e) => {
            if (e.target === registerModal) {
                registerModal.classList.remove('active');
            }
        });
    }

    // Waitlist form handling
    const waitlistForm = document.getElementById('waitlist-form') as HTMLFormElement;
    const waitlistModal = document.querySelector('.waitlist-modal');
    const waitlistSuccess = document.getElementById('waitlist-success');
    const successClose = document.getElementById('success-close');
    const queuePosition = document.getElementById('queue-position');
    const peopleWaiting = document.getElementById('people-waiting');

    if (waitlistForm) {
        waitlistForm.addEventListener('submit', (e) => {
            e.preventDefault();
            const submitBtn = waitlistForm.querySelector('.waitlist-submit');

            if (submitBtn) {
                submitBtn.classList.add('loading');

                // Simulate API call
                setTimeout(() => {
                    submitBtn.classList.remove('loading');

                    // Update queue position
                    const currentWaiting = parseInt(peopleWaiting?.textContent || '243');
                    if (queuePosition) {
                        queuePosition.textContent = String(currentWaiting + 1);
                    }

                    // Show success state
                    if (waitlistModal && waitlistSuccess) {
                        (waitlistModal as HTMLElement).style.display = 'none';
                        waitlistSuccess.style.display = 'block';
                    }
                }, 1500);
            }
        });
    }

    if (successClose && registerModal) {
        successClose.addEventListener('click', () => {
            registerModal.classList.remove('active');
            // Reset form and views after close
            setTimeout(() => {
                if (waitlistModal) {
                    (waitlistModal as HTMLElement).style.display = 'block';
                }
                if (waitlistSuccess) {
                    waitlistSuccess.style.display = 'none';
                }
                if (waitlistForm) {
                    waitlistForm.reset();
                }
            }, 300);
        });
    }

    const infoPopupClose = document.getElementById('info-popup-close');
    const infoPopup = document.getElementById('info-popup');
    if (infoPopupClose && infoPopup) {
        infoPopupClose.addEventListener('click', () => {
            infoPopup.classList.remove('active');
        });
    }

    // Apartment Popup Back Button
    const apartmentPopupBack = document.getElementById('apartment-popup-back');
    const apartmentPopupBackdrop = document.querySelector('.apartment-popup-backdrop');
    if (apartmentPopupBack) {
        apartmentPopupBack.addEventListener('click', () => {
            closeApartmentPopup();
        });
    }
    // Also close on backdrop click
    if (apartmentPopupBackdrop) {
        apartmentPopupBackdrop.addEventListener('click', () => {
            closeApartmentPopup();
        });
    }

    // Camera view buttons
    const tableBBtn = document.getElementById('table-b-btn');
    const tableB1Btn = document.getElementById('table-b1-btn');
    const frontViewBtn = document.getElementById('front-view-btn');
    const topViewBtn = document.getElementById('top-view-btn');
    const freeViewBtn = document.getElementById('free-view-btn');

    // Helper function to find camera by name
    const findCameraByName = (name: string): THREE.Camera | undefined => {
        console.log('Looking for camera:', name);
        console.log('Available cameras:', glbCameras.map(c => c.name));
        const found = glbCameras.find(cam => cam.name.toLowerCase() === name.toLowerCase());
        console.log('Found:', found ? found.name : 'NOT FOUND');
        return found;
    };

    if (tableBBtn) {
        tableBBtn.addEventListener('click', () => {
            console.log('Table B button clicked');
            cameraManager.unlockControls(); // Reset first
            const cam = findCameraByName('camera_b');
            if (cam) {
                // Use 'constrained' mode for Table B
                cameraManager.flyToCamera(cam);
            } else {
                console.warn('Camera "camera_b" not found in GLB. Available:', glbCameras.map(c => c.name));
            }
        });
    }

    if (tableB1Btn) {
        tableB1Btn.addEventListener('click', () => {
            console.log('Table B1 button clicked');
            cameraManager.unlockControls(); // Reset first
            const cam = findCameraByName('camera_b1');
            if (cam) {
                // Use 'constrained' mode for Table B1
                cameraManager.flyToCamera(cam);
            } else {
                console.warn('Camera "camera_b1" not found in GLB. Available:', glbCameras.map(c => c.name));
            }
        });
    }

    if (frontViewBtn) {
        frontViewBtn.addEventListener('click', () => {
            console.log('Front View button clicked');

            cameraManager.unlockControls();
            cameraManager.flyToPoint(new THREE.Vector3(0, 0.1, 0), 5, Math.PI / 2, 0);
        });
    }

    if (topViewBtn) {
        topViewBtn.addEventListener('click', () => {
            console.log('Top View button clicked');

            cameraManager.flyToPoint(new THREE.Vector3(0, 0, 0), 5, 0);
            cameraManager.lockControls();
        });
    }

    if (freeViewBtn) {
        freeViewBtn.addEventListener('click', () => {
            cameraManager.unlockControls();
            cameraManager.flyToPoint(new THREE.Vector3(0, 0.1, 0), 3, Math.PI / 3, Math.PI / 3);

            // cameraManager.unlockControls();
            // cameraManager.fitToModel(model);
        });
    }

    // Transport toggle button
    const transportToggleBtn = document.getElementById('transport-toggle-btn');
    if (transportToggleBtn) {
        transportToggleBtn.addEventListener('click', () => {
            if (transportModel) {
                transportModel.visible = !transportModel.visible;
                transportToggleBtn.classList.toggle('active', transportModel.visible);
                console.log('Transport lines:', transportModel.visible ? 'visible' : 'hidden');
            }
        });
    }

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            if (settingsPanel) settingsPanel.classList.remove('active');
            if (registerModal) {
                registerModal.classList.remove('active');
            }
            if (mobileMenu) {
                mobileMenu.classList.remove('active');
                if (menuBtn) menuBtn.classList.remove('active');
            }
            // Close hotspot editor
            const hotspotEditorModal = document.getElementById('hotspot-editor-modal');
            if (hotspotEditorModal) {
                hotspotEditorModal.classList.remove('active');
            }
        }
    });

    // Hotspot Edit Mode Toggle
    const hotspotEditBtn = document.getElementById('hotspot-edit-btn');
    const hotspotToolsPanel = document.getElementById('hotspot-tools');
    const apartmentSelectorContainer = document.getElementById('apartment-selector-container');

    if (hotspotEditBtn) {
        hotspotEditBtn.addEventListener('click', () => {
            toggleHotspotEditMode();
            // Show/hide tools
            if (hotspotToolsPanel) {
                hotspotToolsPanel.classList.toggle('visible', hotspotEditMode);
            }
            // Show/hide apartment selector
            if (apartmentSelectorContainer) {
                apartmentSelectorContainer.classList.toggle('visible', hotspotEditMode);
            }
        });
    }

    // Apartment Selector
    const apartmentSelector = document.getElementById('apartment-selector') as HTMLSelectElement;
    if (apartmentSelector) {
        apartmentSelector.addEventListener('change', (e) => {
            const index = parseInt((e.target as HTMLSelectElement).value);
            switchApartment(index);
        });
    }

    // Hotspot Editor Modal Controls
    const hotspotEditorModal = document.getElementById('hotspot-editor-modal');
    const hotspotEditorClose = document.getElementById('hotspot-editor-close');
    const hotspotSaveBtn = document.getElementById('hotspot-save-btn');
    const hotspotDeleteBtn = document.getElementById('hotspot-delete-btn');
    const hotspotTitleInput = document.getElementById('hotspot-title-input') as HTMLInputElement;
    const hotspotDescInput = document.getElementById('hotspot-desc-input') as HTMLTextAreaElement;

    if (hotspotEditorClose && hotspotEditorModal) {
        hotspotEditorClose.addEventListener('click', () => {
            hotspotEditorModal.classList.remove('active');
            selectedHotspot = null;
        });
    }

    // New hotspot editor fields
    const hotspotApartmentSelect = document.getElementById('hotspot-apartment-select') as HTMLSelectElement;
    const hotspotMediaTypeSelect = document.getElementById('hotspot-media-type') as HTMLSelectElement;
    const hotspotMediaUrlInput = document.getElementById('hotspot-media-url') as HTMLInputElement;
    const hotspotMediaUrlGroup = document.getElementById('hotspot-media-url-group');
    const hotspotColorInput = document.getElementById('hotspot-color-input') as HTMLInputElement;

    // Media type dropdown toggle
    if (hotspotMediaTypeSelect && hotspotMediaUrlGroup) {
        hotspotMediaTypeSelect.addEventListener('change', () => {
            hotspotMediaUrlGroup.style.display = hotspotMediaTypeSelect.value ? 'block' : 'none';
        });
    }

    if (hotspotSaveBtn && hotspotEditorModal && hotspotTitleInput && hotspotDescInput) {
        hotspotSaveBtn.addEventListener('click', () => {
            if (selectedHotspot) {
                const apartmentIndex = hotspotApartmentSelect?.value ? parseInt(hotspotApartmentSelect.value) : null;
                const mediaType = hotspotMediaTypeSelect?.value as 'image' | 'video' | null || null;
                const mediaUrl = hotspotMediaUrlInput?.value || '';
                const color = hotspotColorInput?.value || selectedHotspot.color;

                updateHotspot(
                    selectedHotspot,
                    hotspotTitleInput.value,
                    hotspotDescInput.value,
                    apartmentIndex,
                    mediaUrl,
                    mediaType,
                    color
                );
                hotspotEditorModal.classList.remove('active');
                selectedHotspot = null;
            }
        });
    }

    if (hotspotDeleteBtn && hotspotEditorModal) {
        hotspotDeleteBtn.addEventListener('click', () => {
            if (selectedHotspot) {
                deleteHotspot(selectedHotspot);
                hotspotEditorModal.classList.remove('active');
                selectedHotspot = null;
            }
        });
    }

    // Export Hotspots
    const exportBtn = document.getElementById('hotspot-export-btn');
    if (exportBtn) {
        exportBtn.addEventListener('click', () => {
            const data = exportHotspots();
            const blob = new Blob([data], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'hotspots.json';
            a.click();
            URL.revokeObjectURL(url);
            console.log('Hotspots exported');
        });
    }

    // Import Hotspots
    const importBtn = document.getElementById('hotspot-import-btn');
    if (importBtn) {
        importBtn.addEventListener('click', () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                        const data = ev.target?.result as string;
                        importHotspots(data);
                    };
                    reader.readAsText(file);
                }
            };
            input.click();
        });
    }
});
